import * as Sentry from '@sentry/react';

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-auth-token'];
const SENSITIVE_FIELDS = ['password', 'token', 'access_token', 'refresh_token', 'credential'];

let initialized = false;

type SentryUser = {
  username: string;
};

type EventLike = {
  request?: {
    headers?: Record<string, unknown>;
    data?: unknown;
  };
  user?: {
    id?: string;
    email?: string;
    username?: string;
    ip_address?: string;
    [key: string]: unknown;
  } | null;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
};

type HintLike = {
  originalException?: unknown;
};

function getSentryDsn(): string {
  return (process.env.REACT_APP_SENTRY_DSN || '').trim();
}

function getSentryEnvironment(): string {
  return (process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'development').trim();
}

function getSentryRelease(): string {
  return (process.env.REACT_APP_SENTRY_RELEASE || '').trim();
}

export function isSentryEnabled(): boolean {
  return process.env.NODE_ENV !== 'test' && getSentryDsn() !== '';
}

export function hashIdentifier(value: string): string {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) + value.charCodeAt(index);
  }

  return `user-${(hash >>> 0).toString(16)}`;
}

function sanitizeHeaders(headers: Record<string, unknown> = {}): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(headers).filter(([name]) => !SENSITIVE_HEADERS.includes(name.toLowerCase()))
  );
}

function sanitizeData(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeData(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, nestedValue]) => (
        SENSITIVE_FIELDS.includes(key.toLowerCase()) ? [] : [[key, sanitizeData(nestedValue)]]
      ))
    );
  }

  return value;
}

export function shouldDropSentryEvent(event: EventLike, hint?: HintLike): boolean {
  const originalException = hint?.originalException as { status?: unknown; name?: unknown; message?: unknown } | undefined;
  const message = event.exception?.values?.map((value) => value.value || value.type || '').join(' ') || '';

  if (typeof originalException?.status === 'number' && [401, 403, 422].includes(originalException.status)) {
    return true;
  }

  if (typeof originalException?.name === 'string' && originalException.name === 'ApiError') {
    const originalMessage = typeof originalException.message === 'string' ? originalException.message : '';

    return /\b(401|403|422)\b/.test(originalMessage);
  }

  return /\b(HTTP 401|HTTP 403|HTTP 422|Invalid credentials)\b/.test(message);
}

export function sanitizeSentryEvent(event: EventLike, hint?: HintLike): EventLike | null {
  if (shouldDropSentryEvent(event, hint)) {
    return null;
  }

  if (event.request) {
    event.request.headers = sanitizeHeaders(event.request.headers);
    event.request.data = sanitizeData(event.request.data);
  }

  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : null;
  }

  return event;
}

export function initSentry(): boolean {
  if (!isSentryEnabled() || initialized) {
    return false;
  }

  const environment = getSentryEnvironment();
  const release = getSentryRelease();

  Sentry.init({
    dsn: getSentryDsn(),
    environment,
    release: release || undefined,
    beforeSend: (event, hint) => sanitizeSentryEvent(event as EventLike, hint as HintLike),
    initialScope: {
      tags: {
        environment,
        release: release || 'unknown',
        service: 'frontend',
      },
    },
    sendDefaultPii: false,
  });

  initialized = true;

  return true;
}

type ScopeLike = {
  setContext: (key: string, context: Record<string, unknown>) => void;
  setExtra: (key: string, extra: unknown) => void;
};

function setScopeContext(scope: ScopeLike, context: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(context)) {
    const sanitized = sanitizeData(value);

    if (sanitized !== null && typeof sanitized === 'object' && !Array.isArray(sanitized)) {
      scope.setContext(key, sanitized as Record<string, unknown>);
      continue;
    }

    scope.setExtra(key, sanitized);
  }
}

export function captureFrontendException(error: unknown, context: Record<string, unknown> = {}): void {
  if (!initialized) {
    return;
  }

  Sentry.withScope((scope) => {
    setScopeContext(scope, context);
    Sentry.captureException(error);
  });
}

export function captureFrontendMessage(message: string, context: Record<string, unknown> = {}): void {
  if (!initialized) {
    return;
  }

  Sentry.withScope((scope) => {
    setScopeContext(scope, context);
    Sentry.captureMessage(message);
  });
}

export function addFrontendBreadcrumb(message: string, data: Record<string, unknown> = {}): void {
  if (!initialized) {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'ui',
    level: 'info',
    message,
    data: sanitizeData(data) as Record<string, unknown>,
  });
}

export function syncSentryUser(user: SentryUser | null): void {
  if (!initialized) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: hashIdentifier(user.username),
  });
}
